import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LaunchIcon from "@mui/icons-material/Launch";
import "./ThreatReviewModalTest.css";

const formatDate = (value) => new Intl.DateTimeFormat("en-US", {
  month: "long", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit"
}).format(new Date(value));

const ThreatReviewModal = ({
  isOpen,
  comments,
  currentIndex,
  onIndexChange,
  onClose,
  onAssessment,
  flagLabels,
  renderHighlightedComment
}) => {
  const [assessmentType, setAssessmentType] = useState("Evaluation");
  const [concernReason, setConcernReason] = useState([]);
  const [mitigations, setMitigations] = useState([]);
  const [analystNote, setAnalystNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");


  const touchStartX = useRef(null);
  const touchCurrentX = useRef(null);

  const comment = comments[currentIndex];

  const hasPrevious =
    currentIndex > 0;

  const hasNext =
    currentIndex >= 0 &&
    currentIndex < comments.length - 1;

  const hasExistingEvaluation =
    Boolean(comment?.evalType);

  const move = (direction) => {
    const nextIndex = currentIndex + direction;
    if (nextIndex >= 0 && nextIndex < comments.length) onIndexChange(nextIndex);
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") move(-1);
      if (event.key === "ArrowRight") move(1);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, currentIndex, comments.length]);

  useEffect(() => {
    if (!comment) return;

    setAssessmentType(
      comment.evalType ||
      "Evaluation"
    );

    setConcernReason(
      Array.isArray(comment.concernReason)
        ? comment.concernReason
        : []
    );

    setMitigations(
      Array.isArray(comment.mitigations)
        ? comment.mitigations
        : []
    );

    setAnalystNote(
      comment.analystNote || ""
    );

    setSaveError("");
    setIsSaving(false);
  }, [
    comment?.id,
    comment?.evalType,
    comment?.evalDate
  ]);
  if (!isOpen || !comment) return null;

  const toggleArrayValue = (setter, value) => setter((current) =>
    current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
  );

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchCurrentX.current === null) return;
    const distance = touchCurrentX.current - touchStartX.current;
    if (Math.abs(distance) > 55) move(distance > 0 ? -1 : 1);
    touchStartX.current = null;
    touchCurrentX.current = null;
  };

  const MITIGATION_VALUE_MAP = {
    "No specific target": "noTarget",
    "Too vague": "tooVague",
    "Protected speech": "1A",
    "Sarcasm or exaggeration": "sarcasm",
    "Reposted content": "other",
    "Other": "other"
  };

  const saveAssessment = async (
    overrideAssessmentType = null
  ) => {
    if (isSaving) {
      return;
    }

    const nextAssessmentType =
      overrideAssessmentType || assessmentType;

    setIsSaving(true);
    setSaveError("");

    const mitigationReason = mitigations
      .map(
        (value) =>
          MITIGATION_VALUE_MAP[value]
      )
      .filter(Boolean);

    const success = await onAssessment({
      commentId: comment.id,
      assessmentType: nextAssessmentType,
      concernReason:
        nextAssessmentType === "False Positive"
          ? []
          : concernReason,
      mitigationReason:
        nextAssessmentType === "False Positive" &&
          mitigationReason.length === 0
          ? ["tooVague"]
          : mitigationReason,
      other: analystNote.trim()
    });

    if (!success) {
      setSaveError(
        "The evaluation could not be saved. Please try again."
      );
      setIsSaving(false);
      return;
    }

    setIsSaving(false);

    if (hasNext) {
      move(1);
    } else {
      onClose();
    }
  };

  return (
    <div className="review-modal-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <button className="review-nav review-nav-left" disabled={!hasPrevious} onClick={() => move(-1)} aria-label="Previous comment">
        <ChevronLeftIcon />
      </button>

      <div
        className="review-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        onTouchStart={(event) => { touchStartX.current = event.touches[0].clientX; touchCurrentX.current = event.touches[0].clientX; }}
        onTouchMove={(event) => { touchCurrentX.current = event.touches[0].clientX; }}
        onTouchEnd={handleTouchEnd}
      >
        <header className="review-modal-header">
          <div>
            <p>Comment {currentIndex + 1} of {comments.length}</p>
            <h2 id="review-modal-title">{comment.assetName}</h2>
            <span>{comment.groupName}</span>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close review"><CloseIcon /></button>
        </header>

        <div className="review-modal-body">
          <section className="review-evidence-panel">
            <div className="review-score-row">
              <div>
                <small>Combined threat value</small>
                <strong>{comment.threatValue}</strong>
              </div>

              <div>
                <small>Predicted threat likelihood</small>
                <strong>{Math.round(comment.llmScore * 100)}%</strong>
              </div>
            </div>

            <blockquote>
              {renderHighlightedComment(
                comment.comment,
                comment.flags
              )}
            </blockquote>

            <div className="review-flags">
              {comment.flags.map((flag) => <span key={flag}>{flagLabels[flag] || flag}</span>)}
            </div>

            <dl className="review-metadata">
              <div><dt>Username</dt><dd>{comment.username}</dd></div>
              <div><dt>Date posted</dt><dd>{formatDate(comment.datePosted)}</dd></div>
              <div><dt>Asset</dt><dd>{comment.assetName}</dd></div>
              <div><dt>Group</dt><dd>{comment.groupName}</dd></div>
              <div><dt>Source</dt><dd>{comment.source}</dd></div>
              <div><dt>Status</dt><dd>{comment.status}</dd></div>
            </dl>

            <a className="source-link" href={comment.url} target="_blank" rel="noreferrer">
              Open original source <LaunchIcon fontSize="small" />
            </a>
          </section>

          <section className="review-assessment-panel">
            <div
              className={`existing-review-banner ${hasExistingEvaluation
                  ? "review-complete"
                  : "review-pending"
                }`}
            >
              <strong>
                {hasExistingEvaluation
                  ? `Previously reviewed: ${comment.evalType}`
                  : "Not yet reviewed"}
              </strong>

              <span>
                {hasExistingEvaluation
                  ? comment.evalDate
                    ? `Saved ${formatDate(comment.evalDate)}`
                    : "An evaluation is already saved."
                  : "Complete the assessment below to mark this comment as reviewed."}
              </span>
            </div>

            <div
              className="assessment-type-control"
              role="group"
              aria-label="Assessment type"
            >
              <button
                type="button"
                className={
                  assessmentType === "Evaluation"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setAssessmentType("Evaluation")
                }
              >
                Evaluation
              </button>

              <button
                type="button"
                className={
                  assessmentType === "Referral"
                    ? "active referral"
                    : ""
                }
                onClick={() =>
                  setAssessmentType("Referral")
                }
              >
                Referral
              </button>

              <button
                type="button"
                className={
                  assessmentType === "False Positive"
                    ? "active false-positive"
                    : ""
                }
                onClick={() => {
                  setAssessmentType("False Positive");
                  setConcernReason([]);
                }}
              >
                False Positive
              </button>
            </div>

            {assessmentType !== "False Positive" && (
              <fieldset>
                <legend>Reason for concern</legend>

                {[
                  "Safety concern",
                  "Direct threat",
                  "Planning or coordination",
                  "Location knowledge",
                  "Grievance",
                  "Harassment",
                  "Other"
                ].map((reason) => (
                  <label key={reason}>
                    <input
                      type="checkbox"
                      checked={concernReason.includes(reason)}
                      onChange={() =>
                        toggleArrayValue(
                          setConcernReason,
                          reason
                        )
                      }
                    />
                    {reason}
                  </label>
                ))}
              </fieldset>
            )}

            <fieldset>
              <legend>Mitigating context</legend>
              {["No specific target", "Too vague", "Protected speech", "Sarcasm or exaggeration", "Reposted content", "Other"].map((reason) => (
                <label key={reason}><input type="checkbox" checked={mitigations.includes(reason)} onChange={() => toggleArrayValue(setMitigations, reason)} />{reason}</label>
              ))}
            </fieldset>

            <label className="analyst-note">
              Analyst note
              <textarea value={analystNote} onChange={(event) => setAnalystNote(event.target.value)} placeholder="Add concise context, collection needs, or next steps..." />
            </label>
          </section>
        </div>

        {saveError && (
          <p
            className="review-save-error"
            role="alert"
          >
            {saveError}
          </p>
        )}

        <footer className="review-modal-footer">
          <span>Use desktop arrows or swipe left and right on mobile.</span>
          <div>
            <button
              type="button"
              className="modal-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>

            <button
              type="button"
              className="modal-false-positive"
              onClick={() =>
                saveAssessment("False Positive")
              }
              disabled={isSaving}
            >
              False Positive
            </button>

            <button
              type="button"
              className="modal-primary"
              onClick={() => saveAssessment()}
              disabled={isSaving}
            >
              {isSaving
                ? "Saving..."
                : hasExistingEvaluation
                  ? "Update and continue"
                  : "Save and continue"}
            </button>
          </div>
        </footer>
      </div>

      <button className="review-nav review-nav-right" disabled={!hasNext} onClick={() => move(1)} aria-label="Next comment">
        <ChevronRightIcon />
      </button>
    </div>
  );
};

export default ThreatReviewModal;
